<template>
  <div class="room" ref="room">
    <Teleport to="#room-header-actions">
      <div class="room-shell-actions">
        <span class="room-shell-code">Room {{ $route.params.roomId }}</span>
        <button ref="inviteButton" type="button" class="room-invite-button" @click="openInvite">
          Invite
        </button>
        <google-cast-launcher aria-label="Cast karaoke"></google-cast-launcher>

        <div v-if="userIdentity" class="room-identity-control">
          <button
            type="button"
            class="room-identity-button"
            :style="{ '--identity-color': userIdentity.userColor }"
            :aria-label="`Singing as ${userIdentity.userName}. Edit display name`"
            @click="handleEditName"
          >
            <span class="room-identity-dot" aria-hidden="true"></span>
            <span class="room-identity-name">{{ userIdentity.userName }}</span>
            <PremiumBadge v-if="!isPremium" size="small" />
          </button>

          <div v-if="isEditingName" class="identity-popover">
            <label for="room-display-name">Display name</label>
            <input
              id="room-display-name"
              v-model="editNameValue"
              class="edit-name-input"
              placeholder="Enter your display name"
              maxlength="20"
              @keyup.enter="saveCustomName"
              @keyup.escape="cancelEditingName"
            />
            <div class="edit-name-actions">
              <button class="save-name-button" @click="saveCustomName" :disabled="isSavingName || !editNameValue.trim()">
                {{ isSavingName ? 'Saving...' : 'Save' }}
              </button>
              <button class="cancel-name-button" @click="cancelEditingName">Cancel</button>
            </div>
            <p v-if="editNameError" class="edit-name-error">{{ editNameError }}</p>
          </div>
        </div>

        <div class="room-more-control" @keydown.esc="closeMoreMenu">
          <button
            ref="moreButton"
            type="button"
            class="room-more-button"
            aria-label="More"
            :aria-expanded="showMoreMenu"
            aria-haspopup="menu"
            @click="toggleMoreMenu"
          >
            <span aria-hidden="true">•••</span>
          </button>
          <div v-if="showMoreMenu" class="room-more-menu" role="menu" aria-label="More options">
            <button type="button" role="menuitem" @click="openInstructionsFromMenu">How it works</button>
            <button type="button" role="menuitem" @click="openFeedbackFromMenu">Feedback</button>
          </div>
        </div>
      </div>
    </Teleport>

    <section class="room-control-panel" aria-label="Find and control songs">
      <SessionClient
        :roomId="$route.params.roomId"
        :karaokePlayerRef="karaokePlayer"
        :isEmbedded="true"
        :playback-controls-target="playbackControlsTarget"
        :host-is-casting="isCasting"
        :host-cast-session-available="isCastSessionAvailable"
      />
    </section>

    <section
      class="room-player-region"
      :class="{ 'room-player-region--expanded': isMobilePlayerExpanded }"
      aria-labelledby="now-playing-title"
    >
      <div class="mobile-player-header">
        <h2 id="now-playing-title">Now Playing</h2>
        <button
          type="button"
          class="mobile-player-toggle"
          :aria-expanded="isMobilePlayerExpanded"
          @click="isMobilePlayerExpanded = !isMobilePlayerExpanded"
        >
          {{ isMobilePlayerExpanded ? 'Collapse player' : 'Expand player' }}
        </button>
      </div>
      <KaraokePlayer
        ref="karaokePlayer"
        :isCastReceiver="false"
        :roomId="$route.params.roomId"
        :isCasting="isCasting"
        surface="sender"
        :analyticsEmitter="analyticsEmitter"
        class="player"
      />
      <div v-if="isCasting" class="cast-overlay">
        <div class="cast-message">
          <img src="@/assets/cast.svg" alt="Cast" class="cast-icon" />
          <h2>My Karaoke World is connected to Cast</h2>
        </div>
      </div>
      <div ref="playbackControlsTarget" class="room-playback-controls" aria-label="Host playback controls"></div>
    </section>

    <RoomInviteModal
      v-if="showInvite"
      :room-id="$route.params.roomId"
      :join-url="joinUrl"
      :copy-success="copySuccess"
      @close="closeInvite"
      @copy="copyToClipboard"
      @share="shareUrl"
    />

    <!-- Instructions Modal -->
    <div v-if="showInstructions" class="modal-overlay" @click="showInstructions = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showInstructions = false">×</button>
        <h2>How does it work?</h2>
        <div class="instructions-content">
          <div class="instruction-step">
            <h3>1. Search & Add Songs</h3>
            <p>Search for your favorite karaoke songs using the search box and add them to the queue.</p>
          </div>
          <div class="instruction-step">
            <h3>2. Share with Friends</h3>
            <p>Share this room's code or link with friends so they can join and add songs to the queue together.</p>
          </div>
          <div class="instruction-step">
            <h3>3. Control Playback</h3>
            <p>Use the remote controls to play, pause, or skip to the next song in the queue.</p>
          </div>
          <div class="instruction-step">
            <h3>4. Cast to TV (Optional)</h3>
            <p>Optionally, click the Cast button to connect to your TV or Chromecast device for a bigger screen experience.</p>
          </div>
        </div>
      </div>
    </div>


    <!-- Move FeedbackButton component here but control its visibility -->
    <FeedbackButton
      :roomId="$route.params.roomId"
      v-if="showFeedbackModal"
      @close="showFeedbackModal = false"
    />

    <!-- Premium Upsell Modal -->
    <PremiumUpsellModal
      v-if="showUpsellModal"
      :feature="upsellFeature"
      @close="showUpsellModal = false"
      @require-sign-in="handleUpsellRequireSignIn"
    />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { config } from '../config'
import { KaraokePlayer } from '@mykaraokeworld/shared'
import { useAnalytics } from '../composables/useAnalytics'
import SessionClient from './SessionClient.vue'
import FeedbackButton from './FeedbackButton.vue'
import PremiumBadge from './PremiumBadge.vue'
import PremiumUpsellModal from './PremiumUpsellModal.vue'
import RoomInviteModal from './RoomInviteModal.vue'
import { useUserIdentity } from '../utils/userIdentity'
import { useAuth } from '../composables/useAuth'
import { useSubscription } from '../composables/useSubscription'

window['__onGCastApiAvailable'] = function(isAvailable) {
  if (isAvailable) {
    initializeCastApi();
  }
};

// Create broadcast channel for cast state sync
let castChannel = new BroadcastChannel('cast_state');

function initializeCastApi() {
  const context = cast.framework.CastContext.getInstance();
  context.setOptions({
    receiverApplicationId: window.CAST_APP_ID,
    autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED
  });
}

export default {
  name: 'RoomDisplay',
  components: {
    KaraokePlayer,
    SessionClient,
    FeedbackButton,
    PremiumBadge,
    PremiumUpsellModal,
    RoomInviteModal
  },
  setup() {
    const route = useRoute();
    const copySuccess = ref(false);
    const copyTimeout = ref(null);
    const castInitialized = ref(false);
    const isCasting = ref(false);
    const isCastSessionAvailable = ref(false);
    const room = ref(null);
    const karaokePlayer = ref(null);
    const playbackControlsTarget = ref(null);
    const showFeedbackModal = ref(false);
    const showInstructions = ref(false);
    const showInvite = ref(false);
    const showMoreMenu = ref(false);
    const isMobilePlayerExpanded = ref(false);
    const showNameInfo = ref(false);
    const nameInfoTimeout = ref(null);
    const { userIdentity } = useUserIdentity(route.params.roomId);
    const { updateCustomDisplayName, isLoggedIn } = useAuth();
    const { isPremium } = useSubscription();
    const isEditingName = ref(false);
    const editNameValue = ref('');
    const editNameError = ref(null);
    const isSavingName = ref(false);
    const showUpsellModal = ref(false);
    const upsellFeature = ref(null);
    const resumeUpsellAfterLogin = ref(false);

    const { trackEvent, createAnalyticsEmitter } = useAnalytics()
    const analyticsEmitter = createAnalyticsEmitter();

    // Log screen view on mount
    onMounted(() => {
      trackEvent('room_visited', {
        room_id: route.params.roomId
      });
    });

    // Rest of the setup function...
    return {
      analyticsEmitter,
      copySuccess,
      copyTimeout,
      castInitialized,
      isCasting,
      isCastSessionAvailable,
      room,
      karaokePlayer,
      playbackControlsTarget,
      showFeedbackModal,
      showInstructions,
      showInvite,
      showMoreMenu,
      isMobilePlayerExpanded,
      showNameInfo,
      nameInfoTimeout,
      userIdentity,
      updateCustomDisplayName,
      isLoggedIn,
      isPremium,
      isEditingName,
      editNameValue,
      editNameError,
      isSavingName,
      showUpsellModal,
      upsellFeature,
      resumeUpsellAfterLogin
    };
  },
  computed: {
    joinUrl() {
      const baseUrl = config.baseUrl || window.location.origin;
      return `${baseUrl}/join/${this.$route.params.roomId}`;
    }
  },
  watch: {
    isLoggedIn(newVal) {
      if (newVal && this.resumeUpsellAfterLogin) {
        this.showUpsellModal = true;
        this.resumeUpsellAfterLogin = false;
      }
    },
    isCasting(newValue) {
      if (newValue) {
        // When casting starts, pause playback and stop queue listener
        const stopPlayer = () => {
          if (this.$refs.karaokePlayer?.player?.getPlayerState) {
            console.log('Stopping video playback');
            this.$refs.karaokePlayer.player.stopVideo();
          } else {
            console.log('Player not ready, retrying in 500ms...');
            setTimeout(stopPlayer, 500);
          }
        };
        
        // Start trying to stop the player
        stopPlayer();

        // Unsubscribe from queue if possible
        if (this.$refs.karaokePlayer?.unsubscribeQueue) {
          console.log('Unsubscribing from queue');
          this.$refs.karaokePlayer.unsubscribeQueue();
        }
      } else {
        // When casting ends, resume queue listener and playback
        if (this.$refs.karaokePlayer) {
          console.log('Setting up queue listener');
          this.$refs.karaokePlayer.setupQueueListener?.();
          
          // Only try to play if we have a current song
          if (this.$refs.karaokePlayer.currentSong && this.$refs.karaokePlayer.player?.playVideo) {
            console.log('Resuming video playback');
            this.$refs.karaokePlayer.playVideo();
          }
        }
      }
    }
  },
  methods: {
    openInvite() {
      this.showInvite = true;
    },
    closeInvite() {
      this.showInvite = false;
      this.$nextTick(() => this.$refs.inviteButton?.focus());
    },
    toggleMoreMenu() {
      this.showMoreMenu = !this.showMoreMenu;
    },
    closeMoreMenu() {
      if (!this.showMoreMenu) return;
      this.showMoreMenu = false;
      this.$nextTick(() => this.$refs.moreButton?.focus());
    },
    openInstructionsFromMenu() {
      this.showMoreMenu = false;
      this.showInstructions = true;
    },
    openFeedbackFromMenu() {
      this.showMoreMenu = false;
      this.showFeedbackModal = true;
    },
    openJoinWindow() {
      const width = 400;
      const height = 800;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      window.open(
        `/join/${this.$route.params.roomId}`,
        `join_${this.$route.params.roomId}`,
        `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes`
      );
    },
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.joinUrl)
        this.copySuccess = true
        
        // Clear any existing timeout
        if (this.copyTimeout) {
          clearTimeout(this.copyTimeout)
        }
        
        // Reset success state after 2 seconds
        this.copyTimeout = setTimeout(() => {
          this.copySuccess = false
        }, 2000)
      } catch (err) {
        console.error('Failed to copy:', err)
      }
    },
    async shareUrl() {
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Join my Karaoke Room',
            text: 'Join my karaoke session!',
            url: this.joinUrl
          })
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('Error sharing:', err)
          }
        }
      } else {
        this.copyToClipboard()
      }
    },
    initializeCastApi() {
      if (!window.chrome || !window.chrome.cast || !window.cast) {
        console.log('Cast API not ready, waiting...');
        setTimeout(this.initializeCastApi, 1000);
        return;
      }
      
      try {
        console.log('Initializing Cast API with App ID:', config.CAST_APP_ID);
        const context = cast.framework.CastContext.getInstance();
        context.setOptions({
          receiverApplicationId: config.CAST_APP_ID,
          autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
          language: 'en-US',
          resumeSavedSession: true
        });

        // Set up session listener
        context.addEventListener(
          cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
          event => {
            console.log('Cast session state changed:', {
              oldState: event.oldState,
              sessionState: event.sessionState,
              errorCode: event.errorCode,
              details: {
                'NO_SESSION': 'No session',
                'SESSION_STARTING': 'Session is starting',
                'SESSION_STARTED': 'Session has started',
                'SESSION_START_FAILED': 'Session failed to start',
                'SESSION_ENDING': 'Session is ending',
                'SESSION_ENDED': 'Session has ended',
                'SESSION_RESUMED': 'Session was resumed'
              }[event.sessionState]
            });

            const session = context.getCurrentSession();
            const sessionId = session?.getSessionId();

            switch (event.sessionState) {
              case cast.framework.SessionState.SESSION_STARTING:
                this.isCastSessionAvailable = false;
                // Cast is already taking over the host UI while the session starts.
                // falls through
              case cast.framework.SessionState.SESSION_STARTED:
              case cast.framework.SessionState.SESSION_RESUMED:
                if (event.sessionState !== cast.framework.SessionState.SESSION_STARTING) {
                  this.isCastSessionAvailable = true;
                }
                console.log('Cast session active');
                this.isCasting = true;
                // Ensure KaraokePlayer is stopped
                this.$nextTick(() => {
                  const stopPlayer = () => {
                    if (this.$refs.karaokePlayer?.player?.getPlayerState) {
                      console.log('Stopping video playback');
                      this.$refs.karaokePlayer.player.stopVideo();
                    } else {
                      console.log('Player not ready, retrying in 500ms...');
                      setTimeout(stopPlayer, 500);
                    }
                  };
                  stopPlayer();
                });
                // Broadcast detailed cast state
                castChannel.postMessage({ 
                  type: 'CAST_STATE_CHANGE', 
                  isCasting: true,
                  sessionState: event.sessionState,
                  sessionId,
                  castState: context.getCastState(),
                  source: 'Room'
                });
                this.sendRoomId();
                break;
              case cast.framework.SessionState.SESSION_ENDED:
              case cast.framework.SessionState.SESSION_START_FAILED:
              case cast.framework.SessionState.SESSION_ENDING:
                console.log('Cast session ended or failed');
                this.isCastSessionAvailable = false;
                this.isCasting = false;
                castChannel.postMessage({ 
                  type: 'CAST_STATE_CHANGE', 
                  isCasting: false,
                  sessionState: event.sessionState,
                  castState: context.getCastState(),
                  source: 'Room'
                });
                break;
            }
          }
        );

        // Also listen for cast state changes
        context.addEventListener(
          cast.framework.CastContextEventType.CAST_STATE_CHANGED,
          event => {
            console.log('Cast state changed:', event);
            const session = context.getCurrentSession();
            // Broadcast cast state change with session info
            castChannel.postMessage({
              type: 'CAST_STATE_CHANGE',
              castState: event.castState,
              sessionId: session?.getSessionId(),
              isCasting: session !== null,
              source: 'Room'
            });
          }
        );

        // Check if we already have an active session
        const currentSession = context.getCurrentSession();
        if (currentSession) {
          console.log('Found existing cast session');
          this.isCastSessionAvailable = true;
          this.isCasting = true;
          castChannel.postMessage({
            type: 'CAST_STATE_CHANGE',
            isCasting: true,
            sessionId: currentSession.getSessionId(),
            sessionState: currentSession.getSessionState(),
            castState: context.getCastState(),
            source: 'Room'
          });
          // Ensure KaraokePlayer is stopped
          this.$nextTick(() => {
            const stopPlayer = () => {
              if (this.$refs.karaokePlayer?.player?.getPlayerState) {
                console.log('Stopping video playback');
                this.$refs.karaokePlayer.player.stopVideo();
              } else {
                console.log('Player not ready, retrying in 500ms...');
                setTimeout(stopPlayer, 500);
              }
            };
            stopPlayer();
          });
        }

        this.castInitialized = true;
        console.log('Cast API initialized successfully');
      } catch (error) {
        console.error('Error initializing Cast API:', error);
        setTimeout(this.initializeCastApi, 1000);
      }
    },
    
    requestCast() {
      if (!this.castInitialized) {
        console.error('Cast API not initialized yet');
        return;
      }

      try {
        const context = cast.framework.CastContext.getInstance();
        const currentSession = context.getCurrentSession();

        if (currentSession) {
          console.log('Session already exists, sending room ID');
          this.sendRoomId();
        } else {
          console.log('Requesting new cast session');
          context.requestSession().then(
            () => {
              console.log('Cast session started');
              this.sendRoomId();
            },
            error => {
              console.error('Error starting cast session:', error);
            }
          );
        }
      } catch (error) {
        console.error('Error requesting cast:', error);
      }
    },

    sendRoomId(retryCount = 0) {
      try {
        const context = cast.framework.CastContext.getInstance();
        const session = context.getCurrentSession();

        if (!session) {
          console.error('No active cast session');
          if (retryCount < 5) {
            console.log(`Retrying sendRoomId in 2s (attempt ${retryCount + 1})`);
            setTimeout(() => this.sendRoomId(retryCount + 1), 2000);
          }
          return;
        }

        const roomId = this.$route.params.roomId;
        console.log(`Sending room ID (attempt ${retryCount + 1}):`, roomId);

        const namespace = 'urn:x-cast:com.google.cast.media';
        session.sendMessage(namespace, {
          type: 'ROOM_ID',
          customData: { roomId }
        }).then(() => {
          console.log('✅ Room ID sent:', roomId);
          // Retry a few times to ensure delivery
          if (retryCount < 3) {
            setTimeout(() => this.sendRoomId(retryCount + 1), (retryCount + 1) * 1000);
          }
        }).catch(err => {
          console.error('❌ sendMessage failed:', err);
          if (retryCount < 5) {
            setTimeout(() => this.sendRoomId(retryCount + 1), 2000);
          }
        });
      } catch (error) {
        console.error('Error in sendRoomId:', error);
      }
    },

    pausePlayback() {
      if (this.$refs.karaokePlayer?.player?.pauseVideo) {
        console.log('Pausing video playback');
        this.$refs.karaokePlayer.player.pauseVideo();
      } else {
        console.log('Player not ready for pause');
      }
    },
    handleEditName() {
      if (!this.isPremium) {
        this.upsellFeature = 'displayName';
        this.showUpsellModal = true;
        return;
      }
      this.startEditingName();
    },
    startEditingName() {
      this.editNameValue = this.userIdentity.isCustomName ? this.userIdentity.userName : '';
      this.editNameError = null;
      this.isEditingName = true;
    },
    async saveCustomName() {
      if (this.isSavingName) return;
      this.isSavingName = true;
      this.editNameError = null;
      const result = await this.updateCustomDisplayName(this.editNameValue);
      if (result.success) {
        this.isEditingName = false;
      } else {
        this.editNameError = result.error;
      }
      this.isSavingName = false;
    },
    cancelEditingName() {
      this.isEditingName = false;
      this.editNameError = null;
    },
    toggleNameInfo() {
      this.showNameInfo = !this.showNameInfo;
      
      // Clear any existing timeout
      if (this.nameInfoTimeout) {
        clearTimeout(this.nameInfoTimeout);
      }
      
      // Set new timeout to auto-dismiss after 5 seconds
      if (this.showNameInfo) {
        this.nameInfoTimeout = setTimeout(() => {
          this.showNameInfo = false;
        }, 5000);
      }
    },
    handleUpsellRequireSignIn() {
      this.resumeUpsellAfterLogin = true
      this.showUpsellModal = false
      window.dispatchEvent(new Event('auth:open-sign-in'))
    }
  },
  mounted() {
    // Check for active cast session immediately
    if (window.chrome && window.chrome.cast && window.cast) {
      try {
        const context = cast.framework.CastContext.getInstance();
        const currentSession = context.getCurrentSession();
        if (currentSession) {
          console.log('Found existing cast session on mount');
          this.isCasting = true;
          // Ensure KaraokePlayer is stopped
          this.$nextTick(() => {
            const stopPlayer = () => {
              if (this.$refs.karaokePlayer?.player?.getPlayerState) {
                console.log('Stopping video playback');
                this.$refs.karaokePlayer.player.stopVideo();
              } else {
                console.log('Player not ready, retrying in 500ms...');
                setTimeout(stopPlayer, 500);
              }
            };
            stopPlayer();
          });
        }
      } catch (error) {
        console.error('Error checking cast session:', error);
      }
    }

    this.initializeCastApi();
    
    // Listen for cast state changes from other windows
    castChannel.onmessage = (event) => {
      if (event.data.type === 'CAST_STATE_CHANGE') {
        console.log('Received cast state change:', event.data);
        this.isCasting = event.data.isCasting;
        
        // Ensure KaraokePlayer state is updated
        this.$nextTick(() => {
          if (this.isCasting) {
            const stopPlayer = () => {
              if (this.$refs.karaokePlayer?.player?.getPlayerState) {
                console.log('Stopping video playback');
                this.$refs.karaokePlayer.player.stopVideo();
              } else {
                console.log('Player not ready, retrying in 500ms...');
                setTimeout(stopPlayer, 500);
              }
            };
            stopPlayer();
          }
        });
        
        // Update cast button state if we have session info and Cast API is available
        if (event.data.sessionId && window.chrome?.cast && window.cast?.framework) {
          try {
            const context = cast.framework.CastContext.getInstance();
            const currentSession = context.getCurrentSession();
            
            // If we don't have this session, create it
            if (!currentSession || currentSession.getSessionId() !== event.data.sessionId) {
              console.log('Syncing cast session state from SessionClient');
              context.setOptions({
                receiverApplicationId: config.CAST_APP_ID,
                autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED
              });
            }
          } catch (error) {
            console.error('Error handling cast state change:', error);
          }
        }
      }
    };
  },
  beforeUnmount() {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
    if (this.nameInfoTimeout) {
      clearTimeout(this.nameInfoTimeout);
    }
    // Clean up broadcast channel
    castChannel.close();
    // Re-create so the module-level variable remains usable if component remounts
    castChannel = new BroadcastChannel('cast_state');
  }
}
</script>

<style scoped>
/* ═══════════════════════════════════════════
   SIDEBAR DESIGN SYSTEM
   Aesthetic: Refined nightlife control panel
   Font: Outfit (geometric, modern)
   Palette: Deep blacks, warm amber (#f0a030),
            soft whites, subtle glass effects
   ═══════════════════════════════════════════ */

.room {
  width: 100vw;
  height: calc(100vh - 60px);
  height: calc(100dvh - 60px);
  display: flex;
  background: #000;
  overflow: hidden;
  margin: 0;
  padding: 0;
  position: fixed;
  top: 60px;
  left: 0;
  font-family: 'Outfit', sans-serif;
}

.room-shell-actions {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.65rem;
}

.room-shell-code {
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.room-invite-button {
  min-height: 36px;
  padding: 0.45rem 1rem;
  border: 0;
  border-radius: 999px;
  background: #ffa500;
  color: #1a0c00;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
}

.room-shell-actions :deep(google-cast-launcher) {
  --connected-color: #ffa500;
  --disconnected-color: rgba(255, 255, 255, 0.72);
  width: 36px;
  height: 36px;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.player {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

:deep(.player-queue-info) {
  order: -1;
  background: rgba(255, 255, 255, 0.06);
  padding: 0.5rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-family: 'Outfit', sans-serif;
}

:deep(.player-container) {
  flex: 1;
  min-height: 0;
  position: relative;
}

/* ─── Sidebar Shell ─── */
.sidebar {
  width: 400px;
  background: linear-gradient(180deg, rgba(18, 14, 10, 0.97) 0%, rgba(10, 8, 6, 0.99) 100%);
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(255, 180, 60, 0.08);
  overflow: hidden;
  position: relative;
  transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 100%;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(255, 165, 0, 0.15) 20%,
    rgba(255, 165, 0, 0.25) 50%,
    rgba(255, 165, 0, 0.15) 80%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 1;
}

.sidebar.collapsed {
  width: 48px;
  padding: 0.5rem;
}

.sidebar.collapsed .room-info {
  display: none;
}

.sidebar.collapsed .sidebar-glow {
  display: none;
}

/* ─── Collapse Button ─── */
.collapse-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: -12px;
  background: rgba(20, 16, 12, 0.95);
  border: 1px solid rgba(255, 180, 60, 0.12);
  border-radius: 20px;
  color: rgba(255, 255, 255, 0.5);
  width: 24px;
  height: 44px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  z-index: 100;
  backdrop-filter: blur(12px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

.sidebar.collapsed .collapse-button {
  right: 44px;
}

.collapse-button:hover {
  background: rgba(255, 165, 0, 0.12);
  border-color: rgba(255, 180, 60, 0.3);
  color: #ffa500;
  box-shadow: 0 2px 12px rgba(255, 165, 0, 0.15);
}

.collapse-button svg {
  width: 8px;
  height: 14px;
}

/* ─── Room Info Container ─── */
.room-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.25rem;
  position: relative;
}

.room-info h1 {
  margin: 0;
  font-family: 'Outfit', sans-serif;
  font-size: 1.75rem;
  font-weight: 600;
  color: white;
  text-align: center;
}

/* ─── Room Header ─── */
.room-header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.room-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.room-id-badge {
  display: flex;
  align-items: center;
  gap: 0;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 6px 12px;
  overflow: hidden;
}

.room-id-label {
  font-family: 'Outfit', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.35);
  margin-right: 8px;
}

.room-id-value {
  font-family: 'Outfit', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.02em;
}

.room-header h1 {
  margin: 0;
  font-family: 'Outfit', sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
}

.room-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

:deep(google-cast-launcher) {
  --connected-color: #ffa500;
  --disconnected-color: rgba(255, 255, 255, 0.6);
  width: 34px;
  height: 34px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

:deep(google-cast-launcher:hover) {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
}

/* ─── User Identity ─── */
.user-identity-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.user-identity-label {
  font-family: 'Outfit', sans-serif;
  font-size: 0.75rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.4);
  padding-left: 2px;
  letter-spacing: 0.02em;
}

.user-identity {
  padding: 10px 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.user-name {
  font-family: 'Outfit', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}

.identity-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.edit-name-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  opacity: 0.6;
  transition: opacity 0.2s, transform 0.2s;
  display: inline-flex;
  align-items: center;
  color: inherit;
}

.edit-name-button:hover {
  opacity: 1;
  transform: scale(1.1);
}

.info-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.info-icon {
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: opacity 0.2s, transform 0.2s;
}

.info-icon:hover {
  opacity: 1;
  transform: scale(1.1);
}

.info-tooltip {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: rgba(10, 8, 6, 0.97);
  color: white;
  padding: 12px 16px;
  border-radius: 10px;
  width: 280px;
  max-width: calc(100vw - 60px);
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 180, 60, 0.1);
  animation: tooltipFadeIn 0.2s ease-out;
}

.info-tooltip p {
  margin: 0;
  font-family: 'Outfit', sans-serif;
  font-size: 0.825rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.8);
}

@keyframes tooltipFadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ─── Edit Name Form ─── */
.edit-name-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.edit-name-input {
  padding: 9px 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-family: 'Outfit', sans-serif;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.edit-name-input:focus {
  outline: none;
  border-color: rgba(255, 165, 0, 0.5);
  box-shadow: 0 0 0 3px rgba(255, 165, 0, 0.08);
}

.edit-name-actions {
  display: flex;
  gap: 8px;
}

.save-name-button {
  padding: 7px 18px;
  border: none;
  border-radius: 8px;
  background: #ffa500;
  color: rgba(0, 0, 0, 0.9);
  font-family: 'Outfit', sans-serif;
  font-weight: 600;
  font-size: 0.825rem;
  cursor: pointer;
  transition: all 0.2s;
}

.save-name-button:hover {
  background: #ffb430;
  box-shadow: 0 2px 8px rgba(255, 165, 0, 0.25);
}

.save-name-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.cancel-name-button {
  padding: 7px 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Outfit', sans-serif;
  font-size: 0.825rem;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-name-button:hover {
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.85);
}

.edit-name-error {
  color: #ff6b6b;
  font-family: 'Outfit', sans-serif;
  font-size: 0.8rem;
  margin: 0;
}

/* ─── Share Section ─── */
.share-section {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
}

.share-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.share-header h2 {
  font-family: 'Outfit', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 165, 0, 0.8);
  margin: 0;
}

.share-chevron {
  display: none;
}

.share-inline-toggle {
  display: none;
}

.share-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.875rem;
  padding: 0 1.25rem 1.25rem;
}

.instructions {
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  font-family: 'Outfit', sans-serif;
  font-size: 0.8rem;
  font-weight: 300;
  margin: 0;
  line-height: 1.5;
}

.join-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.qr-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.url-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
  position: relative;
}

.url-label {
  margin: 0;
  font-family: 'Outfit', sans-serif;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
}

.url-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 0.125rem;
}

.join-url {
  color: rgba(255, 165, 0, 0.85);
  text-decoration: none;
  font-family: 'Outfit', sans-serif;
  font-size: 0.8rem;
  font-weight: 400;
  word-break: break-all;
  text-align: center;
  transition: color 0.2s;
}

.join-url:hover {
  color: #ffa500;
}

.icon {
  width: 16px;
  height: 16px;
  stroke: white;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.action-icon:hover .icon {
  opacity: 1;
}

.action-icon {
  background: none;
  border: 1px solid transparent;
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  transition: all 0.2s;
}

.action-icon:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}

.success-icon {
  font-size: 1.125rem;
  color: #4CAF50;
}

/* ─── Help Button ─── */
.help-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.35);
  font-family: 'Outfit', sans-serif;
  font-size: 0.775rem;
  font-weight: 400;
  cursor: pointer;
  padding: 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: color 0.2s;
  width: 100%;
}

.help-button:hover {
  color: rgba(255, 255, 255, 0.65);
}

.help-button svg {
  opacity: 0.7;
}

/* ─── Embedded Remote ─── */
.embedded-remote {
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* ─── Sidebar Footer ─── */
.sidebar-footer {
  flex-shrink: 0;
  padding: 0.625rem 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

.sidebar.collapsed .sidebar-footer {
  display: none;
}

.feedback-link {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.3);
  padding: 0.5rem 1rem;
  font-family: 'Outfit', sans-serif;
  font-size: 0.775rem;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.25s ease;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.feedback-link:hover {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.6);
}

.feedback-link svg {
  opacity: 0.6;
}

/* ─── Cast Overlay ─── */
.cast-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.cast-message {
  text-align: center;
  color: white;
  padding: 2rem;
}

.cast-message h2 {
  margin: 1rem 0 0;
  font-family: 'Outfit', sans-serif;
  font-size: 1.4rem;
  font-weight: 500;
  color: #ffa500;
}

.cast-icon {
  width: 48px;
  height: 48px;
  filter: invert(1);
  opacity: 0.8;
}

/* ─── Copy Feedback Toast ─── */
.copy-feedback {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(10, 8, 6, 0.95);
  color: rgba(255, 255, 255, 0.9);
  padding: 0.5rem 1.25rem;
  border-radius: 10px;
  font-family: 'Outfit', sans-serif;
  font-size: 0.825rem;
  font-weight: 400;
  pointer-events: none;
  animation: fadeInOut 2s ease-in-out forwards;
  z-index: 1000;
  white-space: nowrap;
  border: 1px solid rgba(255, 180, 60, 0.15);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translate(-50%, 0.75rem); }
  15% { opacity: 1; transform: translate(-50%, 0); }
  85% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -0.5rem); }
}

/* ─── Scrollbar (hidden) ─── */
::-webkit-scrollbar {
  display: none;
}

* {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* ═══════════════════════════════════════════
   RESPONSIVE
   ═══════════════════════════════════════════ */

@media (max-width: 1024px) {
  .room {
    height: 100vh;
    height: 100dvh;
    min-height: -webkit-fill-available;
  }

  .sidebar {
    width: 350px;
  }

  .room-info {
    padding: 1rem;
    gap: 0.875rem;
  }
}

@media (max-width: 768px) {
  .room {
    flex-direction: column;
    position: relative;
    top: auto;
    left: auto;
    width: 100vw;
    height: auto;
    min-height: calc(100vh - 60px);
    min-height: calc(100dvh - 60px);
    overflow-y: auto;
    margin-left: calc(-50vw + 50%);
    margin-top: -60px;
    padding-top: 60px;
  }

  .main-content {
    flex: 0 0 auto;
    height: 30vh;
    min-height: 200px;
    overflow: hidden;
  }

  :deep(.player-header) {
    display: none;
  }

  :deep(.youtube-attribution) {
    display: none;
  }

  .sidebar {
    width: 100%;
    flex: 0 0 auto;
    border-left: none;
    border-top: 1px solid rgba(255, 180, 60, 0.08);
    overflow: visible;
  }

  .sidebar-glow {
    display: none;
  }

  .room-info {
    overflow: visible;
  }

  :deep(.search-section) {
    max-height: none;
  }

  :deep(.search-results) {
    max-height: none;
    overflow-y: visible;
  }

  :deep(.my-music-section) {
    max-height: none;
    overflow-y: visible;
  }

  .sidebar.collapsed {
    width: 100%;
  }

  .collapse-button {
    display: none;
  }

  .sidebar.collapsed .room-info {
    display: flex;
  }

  .sidebar.collapsed .sidebar-footer {
    display: block;
  }

  .sidebar-footer {
    padding: 0.5rem 0.75rem;
    border-top: 1px solid rgba(255, 255, 255, 0.04);
  }

  .room-header {
    margin-bottom: 0;
    gap: 0.5rem;
  }

  .room-id-badge {
    padding: 4px 10px;
  }

  .room-id-label {
    font-size: 0.6rem;
    margin-right: 6px;
  }

  .room-id-value {
    font-size: 0.85rem;
  }

  .room-info {
    gap: 0.5rem;
  }

  .share-inline-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 0.25rem;
    cursor: pointer;
    width: 34px;
    height: 34px;
    transition: all 0.2s;
  }

  .share-inline-toggle:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.04);
  }

  .share-inline-toggle .icon {
    width: 16px;
    height: 16px;
    opacity: 0.6;
  }

  .share-header {
    display: none;
  }

  .share-section {
    display: none;
    border-radius: 10px;
  }

  .share-section.expanded {
    display: block;
  }

  .share-body {
    display: none;
    padding: 1rem;
    gap: 0.75rem;
  }

  .share-section.expanded .share-body {
    display: flex;
  }

  .share-section :deep(canvas) {
    width: 100px !important;
    height: 100px !important;
  }

  .user-identity-container {
    flex-direction: row;
    align-items: center;
    gap: 6px;
  }

  .user-identity-label {
    font-size: 0.7rem;
    padding: 0;
    white-space: nowrap;
  }

  .user-identity {
    padding: 5px 10px;
    border-radius: 8px;
  }

  .user-name {
    font-size: 0.8rem;
  }

  .join-info {
    gap: 0.75rem;
  }

  .url-section {
    gap: 0.5rem;
  }
}

@media (max-width: 480px) {
  .room-info {
    padding: 0.75rem;
    gap: 0.75rem;
  }
}

/* ─── Modal ─── */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(6px);
  padding: 1rem;
}

.modal-content {
  background: rgba(18, 14, 10, 0.97);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 480px;
  width: 100%;
  position: relative;
  border: 1px solid rgba(255, 180, 60, 0.1);
  text-align: left;
  max-height: 85vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6);
}

.modal-close {
  position: absolute;
  top: 0.875rem;
  right: 0.875rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.35);
  font-size: 1.4rem;
  cursor: pointer;
  padding: 0.5rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
}

.modal-content h2 {
  font-family: 'Outfit', sans-serif;
  color: #ffa500;
  margin: 0 0 0.75rem;
  font-size: 1.15rem;
  font-weight: 600;
}

.instructions-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.instruction-step {
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.instruction-step h3 {
  font-family: 'Outfit', sans-serif;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 0.25rem;
  font-size: 0.9rem;
  font-weight: 500;
}

.instruction-step p {
  font-family: 'Outfit', sans-serif;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
  font-size: 0.825rem;
  line-height: 1.5;
  font-weight: 300;
}
</style>

<style scoped>
/* Search-first room composition. Kept last so it cleanly supersedes the legacy
   sidebar presentation while the room's playback and modal logic stays intact. */
.room {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 420px);
  grid-template-areas: "player controls";
  width: 100%;
  height: calc(100dvh - 60px);
  position: fixed;
  inset: 60px 0 0;
  overflow: hidden;
  background: #000;
}

.room-control-panel {
  grid-area: controls;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  border-left: 1px solid rgba(255, 165, 0, 0.12);
  background: linear-gradient(180deg, #15110d 0%, #090806 100%);
}

.room-player-region {
  grid-area: player;
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: #000;
}

.room-player-region :deep(.karaoke-player) {
  bottom: 78px;
}

.room-playback-controls {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 140;
  height: 78px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-top: 1px solid rgba(255, 165, 0, 0.12);
  background: #0b0907;
}

.room-playback-controls :deep(.playback-controls) {
  height: 100%;
  box-sizing: border-box;
  padding: 0.6rem;
}

.room-playback-controls :deep(.playback-controls .playback-button) {
  height: 56px;
  min-height: 56px;
  box-sizing: border-box;
  padding: 0.35rem 0.5rem;
}

.mobile-player-header {
  display: none;
}

.room-shell-actions {
  gap: 0.5rem;
}

.room-identity-control,
.room-more-control {
  position: relative;
}

.room-identity-button,
.room-more-button {
  min-height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.88);
  font: inherit;
  cursor: pointer;
}

.room-identity-button {
  max-width: 180px;
  padding: 0.4rem 0.65rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.room-identity-dot {
  width: 9px;
  height: 9px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: var(--identity-color, #ffa500);
}

.room-identity-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.78rem;
  font-weight: 600;
}

.room-more-button {
  width: 40px;
  padding: 0;
  border-radius: 50%;
  letter-spacing: 0.08em;
}

.room-identity-button:hover,
.room-more-button:hover,
.room-identity-button:focus-visible,
.room-more-button:focus-visible {
  border-color: rgba(255, 165, 0, 0.45);
  background: rgba(255, 165, 0, 0.09);
  outline: none;
}

.identity-popover,
.room-more-menu {
  position: absolute;
  top: calc(100% + 0.55rem);
  right: 0;
  z-index: 1100;
  width: min(290px, calc(100vw - 1.5rem));
  padding: 0.8rem;
  border: 1px solid rgba(255, 165, 0, 0.2);
  border-radius: 12px;
  background: #17120e;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.55);
}

.identity-popover {
  display: grid;
  gap: 0.55rem;
  text-align: left;
}

.identity-popover label {
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.75rem;
  font-weight: 600;
}

.identity-popover .edit-name-form {
  margin: 0;
}

.room-more-menu {
  width: 180px;
  display: grid;
  gap: 0.25rem;
}

.room-more-menu button {
  min-height: 44px;
  padding: 0.65rem 0.75rem;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.82);
  font: inherit;
  font-size: 0.85rem;
  text-align: left;
  cursor: pointer;
}

.room-more-menu button:hover,
.room-more-menu button:focus-visible {
  background: rgba(255, 165, 0, 0.1);
  color: #ffa500;
  outline: none;
}

@media (max-width: 768px) {
  .room {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "controls"
      "player";
    grid-template-rows: max-content auto;
    align-content: start;
    width: 100%;
    height: calc(100dvh - 60px);
    min-height: 0;
    position: fixed;
    inset: 60px 0 0;
    margin: 0;
    padding: 0;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .room-control-panel {
    height: clamp(240px, 34dvh, 320px);
    min-height: 0;
    overflow: hidden;
    border-left: 0;
    border-bottom: 1px solid rgba(255, 165, 0, 0.12);
  }

  .room-player-region {
    height: 328px;
    min-height: 328px;
    transition: height 0.2s ease;
  }

  .room-player-region--expanded {
    height: min(60dvh, 520px);
    min-height: min(60dvh, 520px);
  }

  .mobile-player-header {
    position: absolute;
    inset: 0 0 auto;
    z-index: 150;
    height: 52px;
    padding: 0 0.75rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(8, 7, 6, 0.88);
    backdrop-filter: blur(10px);
  }

  .mobile-player-header h2 {
    margin: 0;
    color: rgba(255, 255, 255, 0.88);
    font-size: 0.9rem;
    font-weight: 600;
  }

  .mobile-player-toggle {
    min-height: 44px;
    padding: 0.45rem 0.65rem;
    border: 0;
    background: transparent;
    color: #ffa500;
    font: inherit;
    font-size: 0.78rem;
    font-weight: 650;
    cursor: pointer;
  }

  .room-player-region :deep(.karaoke-player) {
    top: 52px;
    bottom: 78px;
  }

  .room-player-region :deep(.empty-queue-message) {
    padding: 1rem;
  }

  .room-player-region :deep(.empty-queue-message h2) {
    margin-bottom: 0.5rem;
    font-size: 1.3rem;
  }

  .room-player-region :deep(.empty-queue-message p) {
    font-size: 0.9rem;
  }

  :deep(.youtube-attribution) {
    display: flex;
  }

  .room-shell-code {
    font-size: 0.7rem;
  }

  .room-invite-button {
    min-height: 40px;
    padding-inline: 0.75rem;
  }

  .room-shell-actions :deep(google-cast-launcher) {
    width: 40px;
    height: 40px;
  }

  .room-identity-button {
    width: 40px;
    padding: 0;
    justify-content: center;
  }

  .room-identity-name,
  .room-identity-button :deep(.premium-badge) {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }
}

@media (max-width: 480px) {
  .room-shell-code {
    display: none;
  }

  .room-shell-actions {
    gap: 0.3rem;
  }
}
</style>
